/**
 * Watermark Column Configuration Page
 *
 * Visual interface for configuring watermark columns for incremental sync tables
 */

import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { fetchWatermarkTables, WatermarkTable, bulkUpdateSyncType, updateSyncFrequency, SyncFrequency } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { AlertCircle, CheckCircle2, RefreshCw, Search, Database } from 'lucide-react';
import { WatermarkTablesTable } from '../../components/adf/WatermarkTablesTable';
import { WatermarkConfigDialog } from '../../components/adf/WatermarkConfigDialog';
import { useToast } from '../../hooks/use-toast';
import { ViewGuard } from "@/components/auth/PermissionGuards";
import { PageWrapper } from "@/components/layout/PageWrapper";

export default function WatermarkConfigPage() {
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSyncTier, setFilterSyncTier] = useState<'all' | 'CDC' | 'INCREMENTAL' | 'FULL'>('all');
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch watermark tables
  const {
    data: watermarkData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['watermark-tables'],
    queryFn: fetchWatermarkTables,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  });

  // Filter tables based on search and sync tier filter
  const filteredTables = watermarkData?.tables.filter(table => {
    const matchesSearch = table.tableName.toLowerCase().includes(searchTerm.toLowerCase());

    // Apply sync tier filter
    let matchesSyncTier = true;
    if (filterSyncTier === 'all') {
      matchesSyncTier = true;
    } else if (filterSyncTier === 'CDC') {
      matchesSyncTier = table.syncTier === 'CDC';
    } else if (filterSyncTier === 'INCREMENTAL') {
      matchesSyncTier = table.syncTier === 'INCREMENTAL';
    } else if (filterSyncTier === 'FULL') {
      matchesSyncTier = table.syncTier?.startsWith('FULL_') || false;
    }

    return matchesSearch && matchesSyncTier;
  }) || [];

  // Calculate filter counts
  const allTablesCount = watermarkData?.tables.length || 0;
  const cdcCount = watermarkData?.tables.filter(t => t.syncTier === 'CDC').length || 0;
  const incrementalCount = watermarkData?.tables.filter(t => t.syncTier === 'INCREMENTAL').length || 0;
  const fullCount = watermarkData?.tables.filter(t => t.syncTier?.startsWith('FULL_')).length || 0;

  // Handle table selection
  const handleTableSelect = (tableName: string) => {
    setSelectedTable(tableName);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setSelectedTable(null);
  };

  // Handle successful update
  const handleUpdateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['watermark-tables'] });
    setSelectedTable(null);
  };

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: () =>
      bulkUpdateSyncType(
        Array.from(selectedRows),
        'FULL_REFRESH',
        'Bulk changed to FULL_REFRESH - no appropriate watermark columns available'
      ),
    onSuccess: (data) => {
      toast({
        title: 'Bulk Update Complete',
        description: `Updated ${data.totalUpdated} tables to FULL sync${
          data.totalFailed > 0 ? `. ${data.totalFailed} failed.` : ''
        }`,
        variant: data.totalFailed > 0 ? 'destructive' : 'default'
      });
      queryClient.invalidateQueries({ queryKey: ['watermark-tables'] });
      setSelectedRows(new Set());
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Sync frequency update mutation
  const syncFrequencyMutation = useMutation({
    mutationFn: ({ tableName, syncFrequency }: { tableName: string; syncFrequency: SyncFrequency }) =>
      updateSyncFrequency(tableName, syncFrequency),
    onSuccess: (data) => {
      toast({
        title: 'Sync Frequency Updated',
        description: `${data.updatedTable.tableName} set to ${data.updatedTable.syncFrequency}`
      });
      queryClient.invalidateQueries({ queryKey: ['watermark-tables'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Handle row selection
  const handleRowSelect = (tableName: string, selected: boolean) => {
    const newSelection = new Set(selectedRows);
    if (selected) {
      newSelection.add(tableName);
    } else {
      newSelection.delete(tableName);
    }
    setSelectedRows(newSelection);
  };

  // Handle select all
  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedRows(new Set(filteredTables.map(t => t.tableName)));
    } else {
      setSelectedRows(new Set());
    }
  };

  // Handle bulk mark as full sync
  const handleBulkMarkFullSync = () => {
    if (selectedRows.size === 0) {
      toast({
        title: 'No rows selected',
        description: 'Please select at least one table',
        variant: 'destructive'
      });
      return;
    }
    bulkUpdateMutation.mutate();
  };

  // Handle sync frequency change
  const handleSyncFrequencyChange = (tableName: string, newFrequency: SyncFrequency) => {
    syncFrequencyMutation.mutate({ tableName, syncFrequency: newFrequency });
  };

  return (
    <ViewGuard section="scheduler" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view Watermark Config</p>
        </div>
      </div>
    }>
    <PageWrapper padding="default">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Database className="h-8 w-8" />
              ADF Sync Configuration
            </h1>
            <p className="text-muted-foreground mt-1">
              View and configure sync tiers and frequencies for Oracle replica tables
            </p>
          </div>
          <Button
            onClick={() => refetch()}
            variant="outline"
            size="sm"
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

      {/* Summary Cards */}
      {watermarkData && (
        <div className="grid gap-6 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Tables</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{watermarkData.totalTables}</div>
              <p className="text-xs text-muted-foreground">Active Oracle replica tables</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">CDC Tables</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {watermarkData.tables.filter(t => t.syncTier === 'CDC').length}
              </div>
              <p className="text-xs text-muted-foreground">Change data capture</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Incremental</CardTitle>
              <RefreshCw className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {watermarkData.tables.filter(t => t.syncTier === 'INCREMENTAL').length}
              </div>
              <p className="text-xs text-muted-foreground">Watermark-based sync</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Full Refresh</CardTitle>
              <AlertCircle className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {watermarkData.tables.filter(t => t.syncTier?.startsWith('FULL_')).length}
              </div>
              <p className="text-xs text-muted-foreground">Complete table reload</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filter */}
      <Card>
        <CardHeader>
          <CardTitle>Search and Filter</CardTitle>
          <CardDescription>Filter tables by sync tier or search by name</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 p-6">
          {/* Search Input */}
          <div className="space-y-2">
            <Label htmlFor="search">Search Tables</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                type="text"
                placeholder="Search by table name..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Sync Tier Filter */}
          <div className="flex gap-2">
            <Button
              variant={filterSyncTier === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterSyncTier('all')}
            >
              All ({allTablesCount})
            </Button>
            <Button
              variant={filterSyncTier === 'CDC' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterSyncTier('CDC')}
              className={filterSyncTier === 'CDC' ? '' : 'border-green-300 text-green-700 hover:bg-green-50'}
            >
              CDC ({cdcCount})
            </Button>
            <Button
              variant={filterSyncTier === 'INCREMENTAL' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterSyncTier('INCREMENTAL')}
              className={filterSyncTier === 'INCREMENTAL' ? '' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}
            >
              Incremental ({incrementalCount})
            </Button>
            <Button
              variant={filterSyncTier === 'FULL' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterSyncTier('FULL')}
              className={filterSyncTier === 'FULL' ? '' : 'border-orange-300 text-orange-700 hover:bg-orange-50'}
            >
              Full Refresh ({fullCount})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tables List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Tables ({filteredTables.length})</CardTitle>
              <CardDescription>
                Click frequency badge to toggle between HOURLY and DAILY
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading tables...</span>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-8 text-destructive">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>Error loading tables: {(error as Error).message}</span>
            </div>
          )}

          {!isLoading && !error && filteredTables.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Database className="h-12 w-12 mb-2 opacity-50" />
              <p>No tables found matching your criteria</p>
            </div>
          )}

          {!isLoading && !error && filteredTables.length > 0 && (
            <WatermarkTablesTable
              tables={filteredTables}
              onTableSelect={handleTableSelect}
              selectedRows={selectedRows}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAll}
              onSyncFrequencyChange={handleSyncFrequencyChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Configuration Dialog */}
      {selectedTable && (
        <WatermarkConfigDialog
          tableName={selectedTable}
          isOpen={!!selectedTable}
          onClose={handleDialogClose}
          onSuccess={handleUpdateSuccess}
        />
      )}
      </div>
    </PageWrapper>
    </ViewGuard>
  );
}
